import { Button, Heading, Text } from "@radix-ui/themes";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FEATURE_OPTIONS, SOURCE_OPTIONS } from "./onboarding-constants";
import { TagsCloud } from "./tags-cloud";

interface OnboardingQuestionsProps {
  features: string[];
  sources: string[];
  onFeaturesChange: (features: string[]) => void;
  onSourcesChange: (sources: string[]) => void;
  onContinue: () => void;
  showContinueButton?: boolean;
}

export const OnboardingQuestions = ({
  features,
  sources,
  onFeaturesChange,
  onSourcesChange,
  onContinue,
  showContinueButton = true,
}: OnboardingQuestionsProps) => {
  const { t } = useTranslation();

  // Convert constants to format expected by TagsCloud
  const featureTagOptions = FEATURE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: "label" in opt ? opt.label : t(opt.key),
  }));

  const sourceTagOptions = SOURCE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.key),
  }));

  return (
    <motion.div
      key="questions"
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      <div>
        <Heading size="6" className="mb-4">
          {t("welcome.greetings.what_looking_for")}
        </Heading>
        <TagsCloud
          rawTagOptions={featureTagOptions}
          onChange={(val) => {
            onFeaturesChange(val);
            if (val.length > 0 && features.length === 0) {
              setTimeout(() => {
                window.scrollTo({
                  top: document.body.scrollHeight,
                  behavior: "smooth",
                });
              }, 200);
            }
          }}
        />
      </div>

      <AnimatePresence>
        {features.length > 0 && (
          <motion.div
            key="sources"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Heading size="6" className="mb-4">
              {t("welcome.greetings.how_did_you_hear")}
            </Heading>
            <TagsCloud
              rawTagOptions={sourceTagOptions}
              onChange={(val) => {
                onSourcesChange(val);
                if (val.length > 0 && sources.length === 0) {
                  setTimeout(() => {
                    window.scrollTo({
                      top: document.body.scrollHeight,
                      behavior: "smooth",
                    });
                  }, 200);
                }
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {sources.length > 0 && showContinueButton && (
          <motion.div
            key="continue"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Text className="mb-4 block text-slate-600 dark:text-slate-400">
              {t("welcome.greetings.thanks_for_sharing")}
            </Text>
            <Button size="3" onClick={onContinue} className="hue-animation">
              {t("welcome.greetings.continue_button")}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
